import { AxiosError } from 'axios';
import { ServerResponse, WithAuditDetails } from 'data/types';
import { History } from 'history';
import { Dispatch, Action } from 'redux';
import { RootState } from 'data/root.reducers';
import i18n from 'i18n';
import { showSuccessMessage, showError } from 'shared/toaster';
import { ThunkDispatch } from 'redux-thunk';
import { extractErrorMessage } from '../../utils';
import * as api from './utimacoPacketSwitch.api';
import { getSelectedUtimacoPacketSwitch } from './utimacoPacketSwitch.selectors';
import {
  GET_UTIMACO_PACKET_SWITCH_LIST,
  GET_UTIMACO_PACKET_SWITCH,
  CREATE_UTIMACO_PACKET_SWITCH,
  UPDATE_UTIMACO_PACKET_SWITCH,
  DELETE_UTIMACO_PACKET_SWITCH,
  GO_TO_UTIMACO_PACKET_SWITCH_LIST,
  GO_TO_UTIMACO_PACKET_SWITCH_EDIT,
  GO_TO_UTIMACO_PACKET_SWITCH_VIEW,
  GO_TO_UTIMACO_PACKET_SWITCH_CREATE,
  SELECT_UTIMACO_PACKET_SWITCH,
  DeleteUtimacoPacketSwitchThunkAction,
  UpdateUtimacoPacketSwitchThunkAction,
  CreateUtimacoPacketSwitchThunkAction,
  SET_UTIMACO_PACKET_SWITCH_LOADING,
  SetUtimacoPacketSwitchLoadingAction,
  UtimacoPacketSwitchActionTypes,
  UtimacoPacketSwitch,
  SelectUtimacoPacketSwitchAction,
} from './utimacoPacketSwitch.types';
import { UTIMACO_PACKET_SWITCH_ROUTE, SYSTEM_CONFIGURATION_ROUTE, MISCELLANEOUS } from '../../constants';

export function goToUtimacoPacketSwitchList(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_UTIMACO_PACKET_SWITCH_LIST,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${MISCELLANEOUS}${UTIMACO_PACKET_SWITCH_ROUTE}`);
  };
}

export function goToUtimacoPacketSwitchCreate(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_UTIMACO_PACKET_SWITCH_CREATE,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${MISCELLANEOUS}${UTIMACO_PACKET_SWITCH_ROUTE}/create`);
  };
}

export function goToUtimacoPacketSwitchEdit(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_UTIMACO_PACKET_SWITCH_EDIT,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${MISCELLANEOUS}${UTIMACO_PACKET_SWITCH_ROUTE}/edit`);
  };
}

export function goToUtimacoPacketSwitchView(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_UTIMACO_PACKET_SWITCH_VIEW,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${MISCELLANEOUS}${UTIMACO_PACKET_SWITCH_ROUTE}/view`);
  };
}

export function setUtimacoPacketSwitchLoadingAction(
  isLoading: boolean,
): SetUtimacoPacketSwitchLoadingAction {
  return {
    type: SET_UTIMACO_PACKET_SWITCH_LOADING,
    payload: { isLoading },
  };
}

export function getUtimacoPacketSwitchList(deliveryFunctionId: string): UtimacoPacketSwitchActionTypes {
  return {
    type: GET_UTIMACO_PACKET_SWITCH_LIST,
    payload: api.getUtimacoPacketSwitchList(deliveryFunctionId),
  };
}

export function getUtimacoPacketSwitch(
  deliveryFunctionId: string, id: string,
): UtimacoPacketSwitchActionTypes {
  return {
    type: GET_UTIMACO_PACKET_SWITCH,
    payload: api.getUtimacoPacketSwitch(deliveryFunctionId, id),
  };
}

export function createUtimacoPacketSwitch(
  deliveryFunctionId: string,
  body: WithAuditDetails<UtimacoPacketSwitch>,
  history: History,
): CreateUtimacoPacketSwitchThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: CREATE_UTIMACO_PACKET_SWITCH,
        payload: api.createUtimacoPacketSwitch(deliveryFunctionId, body),
      });

      dispatch(getUtimacoPacketSwitchList(deliveryFunctionId));
      dispatch(goToUtimacoPacketSwitchList(history));
      showSuccessMessage(i18n.t('utimacoPacketSwitch.create.success'), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('utimacoPacketSwitch.create.failed', { error: errorMessage }));
    }
  };
}

export function createUtimacoPacketSwitchFromForm(
  deliveryFunctionId: string,
  body: WithAuditDetails<UtimacoPacketSwitch>,
): CreateUtimacoPacketSwitchThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    dispatch(setUtimacoPacketSwitchLoadingAction(true));
    try {
      await dispatch({
        type: CREATE_UTIMACO_PACKET_SWITCH,
        payload: api.createUtimacoPacketSwitch(deliveryFunctionId, body),
      });

      showSuccessMessage(i18n.t('utimacoPacketSwitch.create.success'), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('utimacoPacketSwitch.create.failed', { error: errorMessage }));

    } finally {
      dispatch(setUtimacoPacketSwitchLoadingAction(false));
      dispatch(getUtimacoPacketSwitchList(deliveryFunctionId));
    }
  };
}

export function updateUtimacoPacketSwitch(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<UtimacoPacketSwitch>,
  history: History,
): UpdateUtimacoPacketSwitchThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: UPDATE_UTIMACO_PACKET_SWITCH,
        payload: api.updateUtimacoPacketSwitch(deliveryFunctionId, id, body),
      });

      dispatch(getUtimacoPacketSwitchList(deliveryFunctionId));
      dispatch(goToUtimacoPacketSwitchList(history));
      showSuccessMessage(i18n.t('utimacoPacketSwitch.update.success'), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('utimacoPacketSwitch.update.failed', { error: errorMessage }));
    }
  };
}

export function deleteUtimacoPacketSwitch(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<void>,
  history?: History,
): DeleteUtimacoPacketSwitchThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>, getState: () => RootState) => {
    const [selectedUtimacoPacketSwitch] = getSelectedUtimacoPacketSwitch(getState());

    try {
      await dispatch({
        type: DELETE_UTIMACO_PACKET_SWITCH,
        payload: api.deleteUtimacoPacketSwitch(deliveryFunctionId, id, body),
      });

      showSuccessMessage(i18n.t('utimacoPacketSwitch.delete.success'), 5000);
      if (history != null) {
        dispatch(goToUtimacoPacketSwitchList(history));
      }
    } catch (e) {
      showError(i18n.t('utimacoPacketSwitch.delete.failed'));

    } finally {
      dispatch(getUtimacoPacketSwitchList(deliveryFunctionId));
    }
  };
}

export function selectUtimacoPacketSwitch(
  utimacoPacketSwitch: UtimacoPacketSwitch[],
): SelectUtimacoPacketSwitchAction {
  return {
    type: SELECT_UTIMACO_PACKET_SWITCH,
    payload: utimacoPacketSwitch,
  };
}
